import { useState } from "react";
import { useForm } from "@inertiajs/react";
import { BackofficeLayout } from "@/components/layout/BackofficeLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { formatCurrency, cn } from "@/lib/utils";
import {
  Check,
  ArrowRight,
  Clock,
  DollarSign,
  ChevronsUpDown,
} from "lucide-react";

type Account = { id: string; name: string; slug: string };
type CountryOption = { id: string; code: string; name: string };
type StateOption = {
  id: string;
  code: string;
  name: string;
  country_id: string;
};
type EntityOption = {
  id: string;
  code: string;
  name: string;
  jurisdiction_code: string;
  country_id: string;
  state_id: string | null;
  state_fee_cents: number;
  service_fee_cents: number;
  total_cents: number;
  processing_days_min: number;
  processing_days_max: number;
  name_options: { id: string; label: string; suffix: string }[];
};

type Props = {
  accounts: Account[];
  countries: CountryOption[];
  states: StateOption[];
  entities: EntityOption[];
};

export default function BackofficeFormationsNew({
  accounts,
  countries,
  states,
  entities,
}: Props) {
  const [selectedAccountId, setSelectedAccountId] = useState("");
  const [accountOpen, setAccountOpen] = useState(false);
  const [selectedCountryId, setSelectedCountryId] = useState("");
  const [selectedStateId, setSelectedStateId] = useState("");
  const [selectedEntityId, setSelectedEntityId] = useState("");

  const selectedAccount = accounts.find((a) => a.id === selectedAccountId);

  const countryStates = states.filter(
    (s) => s.country_id === selectedCountryId,
  );
  const hasStates = countryStates.length > 0;

  const filteredEntities = entities.filter((e) => {
    if (!selectedCountryId) return false;
    if (e.country_id !== selectedCountryId) return false;
    if (hasStates && selectedStateId && e.state_id !== selectedStateId)
      return false;
    if (hasStates && !selectedStateId) return false;
    return true;
  });

  const selectedEntity = entities.find((e) => e.id === selectedEntityId);

  const form = useForm({
    account_id: "",
    entity_id: "",
    legal_name: "",
    assumed_name: "",
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    form.post("/backoffice/formations");
  };

  return (
    <BackofficeLayout
      title="New Formation"
      breadcrumbs={[
        { label: "Backoffice", href: "/backoffice/dashboard" },
        { label: "Formations", href: "/backoffice/formations" },
        { label: "New Formation" },
      ]}
    >
      <div className="mb-6">
        <h1 className="text-xl font-semibold tracking-tight">New Formation</h1>
        <p className="text-sm text-muted-foreground">
          Start a formation and assign it to a workspace
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6 max-w-xl">
        {/* Step 0 — Workspace */}
        <section>
          <div className="flex items-center gap-2 mb-3">
            <span className="w-5 h-5 rounded-full bg-muted flex items-center justify-center text-xs font-medium text-muted-foreground">
              1
            </span>
            <h2 className="text-base font-medium text-foreground">Workspace</h2>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs text-muted-foreground">
              Assign to workspace
            </Label>
            <Popover open={accountOpen} onOpenChange={setAccountOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  role="combobox"
                  aria-expanded={accountOpen}
                  className="w-full justify-between font-normal"
                >
                  {selectedAccount
                    ? selectedAccount.name
                    : "Select workspace..."}
                  <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-full p-0" align="start">
                <Command>
                  <CommandInput placeholder="Search workspaces..." />
                  <CommandList>
                    <CommandEmpty>No workspace found.</CommandEmpty>
                    <CommandGroup>
                      {accounts.map((a) => (
                        <CommandItem
                          key={a.id}
                          value={a.name}
                          onSelect={() => {
                            setSelectedAccountId(a.id);
                            form.setData("account_id", a.id);
                            setAccountOpen(false);
                          }}
                        >
                          <Check
                            className={cn(
                              "mr-2 h-4 w-4",
                              selectedAccountId === a.id
                                ? "opacity-100"
                                : "opacity-0",
                            )}
                          />
                          {a.name}
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          </div>
        </section>

        {/* Step 1 — Jurisdiction */}
        {selectedAccountId && (
          <section>
            <div className="flex items-center gap-2 mb-3">
              <span className="w-5 h-5 rounded-full bg-muted flex items-center justify-center text-xs font-medium text-muted-foreground">
                2
              </span>
              <h2 className="text-base font-medium text-foreground">
                Jurisdiction
              </h2>
            </div>

            <div
              className={`grid gap-3 ${hasStates ? "grid-cols-2" : "grid-cols-1"}`}
            >
              <div className="space-y-1.5">
                <Label className="text-xs text-muted-foreground">Country</Label>
                <Select
                  value={selectedCountryId}
                  onValueChange={(value) => {
                    setSelectedCountryId(value);
                    setSelectedStateId("");
                    setSelectedEntityId("");
                    form.setData("entity_id", "");
                  }}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Select country..." />
                  </SelectTrigger>
                  <SelectContent>
                    {countries.map((c) => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {hasStates && (
                <div className="space-y-1.5">
                  <Label className="text-xs text-muted-foreground">State</Label>
                  <Select
                    value={selectedStateId}
                    onValueChange={(value) => {
                      setSelectedStateId(value);
                      setSelectedEntityId("");
                      form.setData("entity_id", "");
                    }}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Select state..." />
                    </SelectTrigger>
                    <SelectContent>
                      {countryStates.map((s) => (
                        <SelectItem key={s.id} value={s.id}>
                          {s.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
          </section>
        )}

        {/* Step 2 — Entity Type */}
        {filteredEntities.length > 0 && (
          <section>
            <div className="flex items-center gap-2 mb-3">
              <span className="w-5 h-5 rounded-full bg-muted flex items-center justify-center text-xs font-medium text-muted-foreground">
                3
              </span>
              <h2 className="text-base font-medium text-foreground">
                Business Type
              </h2>
            </div>

            <div className="space-y-1.5">
              {filteredEntities.map((entity) => {
                const isSelected = selectedEntityId === entity.id;
                return (
                  <button
                    key={entity.id}
                    type="button"
                    onClick={() => {
                      setSelectedEntityId(entity.id);
                      form.setData("entity_id", entity.id);
                    }}
                    className={`w-full p-3 text-left rounded-lg border transition-all ${
                      isSelected
                        ? "border-foreground bg-muted"
                        : "border-border hover:border-border"
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-foreground">
                            {entity.name}
                          </span>
                          {isSelected && (
                            <Check className="w-4 h-4 text-foreground" />
                          )}
                        </div>
                        <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {entity.processing_days_min}-
                            {entity.processing_days_max} days
                          </span>
                          <span className="flex items-center gap-1">
                            <DollarSign className="w-3 h-3" />
                            {formatCurrency(entity.total_cents)}
                          </span>
                        </div>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
            {form.errors.entity_id && (
              <p className="mt-3 text-[12px] text-destructive">
                {form.errors.entity_id}
              </p>
            )}
          </section>
        )}

        {/* Step 3 — Company Details */}
        {selectedEntity && (
          <section>
            <div className="flex items-center gap-2 mb-3">
              <span className="w-5 h-5 rounded-full bg-muted flex items-center justify-center text-xs font-medium text-muted-foreground">
                4
              </span>
              <h2 className="text-base font-medium text-foreground">
                Company Details
              </h2>
            </div>

            <div className="space-y-3">
              <div className="space-y-1.5">
                <Label htmlFor="legal_name">Company Name</Label>
                <Input
                  id="legal_name"
                  type="text"
                  value={form.data.legal_name}
                  onChange={(e) => form.setData("legal_name", e.target.value)}
                  placeholder="Enter legal name"
                />
                {form.errors.legal_name && (
                  <p className="text-[12px] text-destructive">
                    {form.errors.legal_name}
                  </p>
                )}
                <p className="text-xs text-muted-foreground">
                  The official legal name for state documents
                </p>
              </div>

              {selectedEntity.name_options.length > 0 ? (
                <div className="space-y-1.5">
                  <Label>Name Suffix</Label>
                  <div className="flex flex-wrap gap-1.5">
                    {selectedEntity.name_options.map((opt) => {
                      const isActive = form.data.assumed_name === opt.suffix;
                      return (
                        <button
                          key={opt.id}
                          type="button"
                          onClick={() =>
                            form.setData(
                              "assumed_name",
                              isActive ? "" : opt.suffix,
                            )
                          }
                          className={`px-3 py-1.5 text-sm rounded-md border transition-all ${
                            isActive
                              ? "border-foreground bg-muted font-medium"
                              : "border-border hover:border-foreground/40"
                          }`}
                        >
                          {opt.label}
                        </button>
                      );
                    })}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {form.data.assumed_name
                      ? `Full name: ${form.data.legal_name || "..."} ${form.data.assumed_name}`
                      : "Select a suffix for your company name"}
                  </p>
                </div>
              ) : (
                <div className="space-y-1.5">
                  <Label htmlFor="assumed_name">
                    DBA Name{" "}
                    <span className="text-muted-foreground font-normal">
                      (Optional)
                    </span>
                  </Label>
                  <Input
                    id="assumed_name"
                    type="text"
                    value={form.data.assumed_name}
                    onChange={(e) =>
                      form.setData("assumed_name", e.target.value)
                    }
                    placeholder="Doing Business As"
                  />
                  <p className="text-xs text-muted-foreground">
                    The name customers will see
                  </p>
                </div>
              )}
            </div>
          </section>
        )}

        {/* Step 4 — Review & Submit */}
        {selectedEntity && form.data.legal_name && (
          <section>
            <div className="flex items-center gap-2 mb-3">
              <span className="w-5 h-5 rounded-full bg-muted flex items-center justify-center text-xs font-medium text-muted-foreground">
                5
              </span>
              <h2 className="text-base font-medium text-foreground">
                Review & Submit
              </h2>
            </div>

            <div className="bg-muted rounded-xl p-4 space-y-3">
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">
                    {selectedEntity.name}
                  </span>
                  <span className="font-medium">
                    {formatCurrency(selectedEntity.total_cents)}
                  </span>
                </div>
                <div className="flex justify-between text-muted-foreground pl-4 text-xs">
                  <span>Service Fee</span>
                  <span>
                    {formatCurrency(selectedEntity.service_fee_cents)}
                  </span>
                </div>
                <div className="flex justify-between text-muted-foreground pl-4 text-xs">
                  <span>State Filing Fee</span>
                  <span>{formatCurrency(selectedEntity.state_fee_cents)}</span>
                </div>
                <div className="h-px bg-border" />
                <div className="flex justify-between font-medium">
                  <span>Total</span>
                  <span>{formatCurrency(selectedEntity.total_cents)}</span>
                </div>
              </div>

              <div className="text-xs text-muted-foreground border-t pt-3 mt-1">
                This formation will be assigned to{" "}
                <span className="font-medium text-foreground">
                  {accounts.find((a) => a.id === selectedAccountId)?.name ||
                    "—"}
                </span>
              </div>

              <Button
                type="submit"
                className="w-full h-9 text-sm"
                disabled={form.processing}
              >
                {form.processing ? "Creating..." : "Create Formation"}
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </section>
        )}
      </form>
    </BackofficeLayout>
  );
}
